import { AdvertisementCell } from "../../advertising/tableCells/AdvertisementCell";
import { css } from "../../../../styled-system/css";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { type AdvertisementQueryV4Row } from "../api/ads/v4AdvReportByAd";
import { useUpdateAdState } from "../../advertising/api/useUpdateAdState";
import { isCampaignStateError } from "../../advertising/api/campaignStateErrors";
import { Switch } from "../../../components/fields/base/Switch";
import { errorToast } from "../../../utils/toasts";
import { useAppSelector } from "../../../redux/hooks";
import { useDailyBudgetMinimum } from "../../../components/core/blocks/AdPlan/utils";
import { useInitialValueSync } from "../../../utils/formily";
import { useAcosOrRoas } from "../../../utils/acosOrRoas";
import { formatCurrency } from "../../../utils/formatNumber";
import { Field } from "@formily/react";
import { NumberInput } from "../../../components/fields/formily/NumberInput";
import { CurrencyInput } from "../../../components/fields/formily/NumberInput";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { GOALS } from "../../../components/core/blocks/AdPlan/constants";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { servingStatusOptions } from "../../advertising/filters/adLifecycleFilters";
import { useSimpleCan } from "../../../config/casl/casl";
import { useModuleEnabled } from "../../../redux/selectors/modules";

const columnHelper = createColumnBuilder<AdvertisementQueryV4Row>();

const isAdNotEditable = (ad: AdvertisementQueryV4Row) => {
  const nonManaged = ad.ad_goal === GOALS.IMPORTED_ADS.value;
  const enrolled = ad.non_trellis_enrolled_ad;
  const isMultiMarketplaceAd = ad.multi_marketplace;
  // Dont show data for this field if ad is non-managed and not enrolled
  if ((nonManaged && !enrolled) || isMultiMarketplaceAd) {
    return true;
  }
  return false;
};

const useAdReportColumns = () => {
  const { t } = useTranslation(["perf_reports", "advertising"]);
  const dateFormatter = useDateFormatter("medium");
  const acosOrRoas = useAcosOrRoas();
  const standardMetricIds = useStandardEntityMetrics("mediaPlan");
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");
  const columns = useMemo(
    () => [
      // Fake columns for filtering purposes
      tagsEnhancedEnabled ?
        columnHelper.accessor("tags_enhanced", {
          id: "tags_enhanced.id",
          header: t(($) => $.perf_reports["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
              label: t(($) => $.perf_reports["Tags"]),
            },
          },
        })
      : columnHelper.accessor("tags", {
          id: "tags",
          header: t(($) => $.perf_reports["Tags"]),
          enableHiding: false,
          meta: {
            columnFilterOptions: {
              format: "multi-select",
            },
          },
        }),
      columnHelper.accessor("category_id", {
        id: "category_id",
        header: t(($) => $.perf_reports["Category"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),

      columnHelper.accessor("ad_status", {
        id: "ad_status",
        header: t(($) => $.perf_reports["Status"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("ntm_enrollment_status", {
        id: "ntm_enrollment_status",
        header: t(($) => $.perf_reports["NTM Ad Status"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("ad_source", {
        id: "ad_source",
        header: t(($) => $.perf_reports["Ad Source"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("bid_algo", {
        id: "bid_algo",
        header: t(($) => $.perf_reports["Bidding Strategy"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("program_id", {
        id: "program_id",
        header: t(($) => $.perf_reports["Bidding Programs"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor("day_parting_method", {
        id: "day_parting_method",
        header: t(($) => $.perf_reports["Dayparting"]),
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      // Fake column for filtering purposes end
      columnHelper.accessor("ad_name", {
        header: t(($) => $.perf_reports["Advertisement"]),
        enableHiding: false,
        cell: (props) => {
          return (
            <AdvertisementCell ad={props.row.original} className={css({ marginRight: "auto", overflowX: "hidden" })} />
          );
        },
        minSize: 75,
        size: 300,
        meta: {
          columnFilterOptions: {
            label: t(($) => $.perf_reports["Advertisement"]),
          },
        },
      }),
      columnHelper.accessor("state", {
        id: "state",
        header: t(($) => $.perf_reports["State"]),
        enableResizing: false,
        size: 70,
        cell: function StateCell({ getValue, row }) {
          const value = getValue();
          // Updating cache from mutation is a PITA, since query key is super complicated. Let's just use a local state
          const [switchState, setSwitchState] = useState(Boolean(value));
          if (Boolean(value) !== switchState) {
            setSwitchState(Boolean(value));
          }
          const stateMutation = useUpdateAdState();

          if (row.original.is_composite || ![0, 1].includes(value)) {
            return "-";
          }

          return (
            <Switch
              isSelected={switchState}
              isPending={stateMutation.isPending}
              isDisabled={!userCanUpdateAds || row.original.multi_marketplace}
              onChange={(isSelected) => {
                stateMutation.mutate(
                  {
                    id: row.original.ad_id,
                    state: isSelected ? 1 : 0,
                  },
                  {
                    onSuccess: () => {
                      setSwitchState(isSelected);
                    },
                    onError: (error) => {
                      errorToast({
                        message:
                          isCampaignStateError(error, "advertisement_not_found") ?
                            t(
                              ($) =>
                                $.perf_reports[
                                  "This advertisement is not in your current account. Refresh the page and try again."
                                ],
                            )
                          : t(($) => $.perf_reports["Failed to update advertisement state"]),
                        error,
                      });
                    },
                  },
                );
              }}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
        },
      }),
      columnHelper.accessor("daily_budget", {
        id: "daily_budget",
        header: t(($) => $.perf_reports["Daily Budget"]),
        size: 90,
        cell: function Cell({ getValue, column, row }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          const minBudget = useDailyBudgetMinimum(row.original);
          useInitialValueSync(column.id, getValue());

          if (isAdNotEditable(row.original)) {
            return formatCurrency(getValue(), {
              marketPlace: marketPlace,
              decimalPlaces: 0,
            });
          }
          return (
            <Field
              name={`${column.id}`}
              initialValue={getValue()}
              validator={(value: number) => {
                if (minBudget && value < minBudget) {
                  return t(($) => $.perf_reports["Budget must be at least {amount}"], {
                    amount: formatCurrency(minBudget, {
                      marketPlace: marketPlace,
                      decimalPlaces: 0,
                    }),
                  });
                }
              }}
              component={[
                CurrencyInput,
                {
                  variant: "inline",
                  css: css.raw({ marginX: "small" }),
                },
              ]}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
          justify: "center",
        },
      }),
      acosOrRoas(
        columnHelper.accessor((r) => r.target_acos, {
          id: "target_acos",
          header: t(($) => $.perf_reports["ACOS Range Low"]),
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value: number) => {
                  if (value <= 0) {
                    return t(($) => $.perf_reports["ACOS Range Low must be greater than 0"]);
                  }
                }}
                reactions={(field) => {
                  const maxAcos = field.query(".max_acos").value() ?? row.original.max_acos;
                  if (field.value && maxAcos && field.value >= maxAcos) {
                    field.selfErrors = [t(($) => $.perf_reports["ACOS Range Low must be less than ACOS Range High"])];
                  } else if (field.value > 0) {
                    field.selfErrors = [];
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    css: css.raw({ marginX: "small" }),
                    endIcon: "%",
                  },
                ]}
              />
            );
          },
          meta: {
            columnFilterOptions: {
              format: "percent",
            },
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
        columnHelper.accessor((r) => r.target_roas, {
          id: "target_roas",
          header: t(($) => $.perf_reports["ROAS Range High"]),
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value: number) => {
                  if (value <= 0) {
                    return t(($) => $.perf_reports["ROAS Range High must be greater than 0"]);
                  }
                }}
                reactions={(field) => {
                  const minRoas = field.query(".min_roas").value() ?? row.original.min_roas;
                  if (field.value && minRoas && field.value <= minRoas) {
                    field.selfErrors = [
                      t(($) => $.perf_reports["ROAS Range High must be greater than ROAS Range Low"]),
                    ];
                  } else if (field.value > 0) {
                    field.selfErrors = [];
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    css: css.raw({ marginX: "small" }),
                  },
                ]}
              />
            );
          },
          meta: {
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
      ),
      acosOrRoas(
        columnHelper.accessor((r) => r.max_acos, {
          id: "max_acos",
          header: t(($) => $.perf_reports["ACOS Range High"]),
          size: 90,

          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value: number) => {
                  if (value <= 0) {
                    return t(($) => $.perf_reports["ACOS Range High must be greater than 0"]);
                  }
                }}
                reactions={(field) => {
                  const targetAcos = field.query(".target_acos").value() ?? row.original.target_acos;
                  if (field.value && targetAcos && field.value <= targetAcos) {
                    field.selfErrors = [
                      t(($) => $.perf_reports["ACOS Range High must be greater than ACOS Range Low"]),
                    ];
                  } else if (field.value > 0) {
                    field.selfErrors = [];
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    css: css.raw({ marginX: "small" }),
                    endIcon: "%",
                  },
                ]}
              />
            );
          },
          meta: {
            columnFilterOptions: {
              format: "percent",
            },
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
        columnHelper.accessor((r) => r.min_roas, {
          id: "min_roas",
          header: t(($) => $.perf_reports["ROAS Range Low"]),
          size: 90,
          cell: function Cell({ getValue, column, row }) {
            useInitialValueSync(column.id, getValue());
            if (isAdNotEditable(row.original)) {
              return "-";
            }
            return (
              <Field
                name={`${column.id}`}
                initialValue={getValue()}
                validator={(value: number) => {
                  if (value <= 0) {
                    return t(($) => $.perf_reports["ROAS Range Low must be greater than 0"]);
                  }
                }}
                reactions={(field) => {
                  const targetRoas = field.query(".target_roas").value() ?? row.original.target_roas;
                  if (field.value && targetRoas && field.value >= targetRoas) {
                    field.selfErrors = [t(($) => $.perf_reports["ROAS Range Low must be less than ROAS Range High"])];
                  } else if (field.value > 0) {
                    field.selfErrors = [];
                  }
                }}
                component={[
                  NumberInput,
                  {
                    variant: "inline",
                    css: css.raw({ marginX: "small" }),
                  },
                ]}
              />
            );
          },
          meta: {
            columnSelectorOptions: {
              grouping: "configuration",
            },
            justify: "center",
          },
        }),
      ),
      columnHelper.accessor("min_bid", {
        id: "min_bid",
        header: t(($) => $.perf_reports["Min Bid"]),
        size: 90,
        cell: function Cell({ getValue }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          return formatCurrency(getValue(), { marketPlace });
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
          justify: "center",
        },
      }),
      columnHelper.accessor("max_bid", {
        id: "max_bid",
        header: t(($) => $.perf_reports["Max Bid"]),
        size: 90,
        cell: function Cell({ getValue }) {
          const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
          return formatCurrency(getValue(), { marketPlace });
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
          justify: "center",
        },
      }),
      columnHelper.accessor("created_datetime", {
        id: "created_datetime",
        header: t(($) => $.perf_reports["Created On"]),
        cell: ({ getValue }) => dateFormatter.format(new Date(getValue())),
        meta: {
          columnFilterOptions: {
            format: "date",
          },
        },
      }),
      columnHelper.accessor("serving_status", {
        id: "serving_status",
        header: t(($) => $.perf_reports["Serving Status"]),
        cell: ({ getValue }) => {
          const label = servingStatusOptions.find((option) => option.value === getValue())?.label;
          return label ? t(label) : null;
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => columnHelper.metric(metricId)),
    ],
    [acosOrRoas, standardMetricIds, userCanUpdateAds, tagsEnhancedEnabled, dateFormatter, t],
  );
  return columns;
};

export default useAdReportColumns;
